import React from "react";
import clsx from "clsx";
import { Disclosure, DisclosureButton, DisclosurePanel, Field, Label, Description, Switch } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ChevronRight, Info, X } from "lucide-react";

/**
 * The small building blocks the document panel's cards, banners and
 * accordions share, so each card file only says what it shows.
 *
 * Plain Tailwind 3 classes rather than catalyst-ui-kit controls: those are
 * styled for Tailwind 4 and render broken here (see invoice-supplier-create.tsx).
 */

/** The panel's way of running a change: busy while it runs, then a quiet
 * reload of the document; a refusal lands in the panel's error banner. */
export type RunChange = (change: () => Promise<unknown>) => Promise<void>;

// On a phone the buttons are 44px tall and the fields 16px, which keeps iOS
// from zooming the page in when a field is focused.
export const buttonClass =
  "inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-11";
export const primaryButtonClass =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-11";
export const criticalButtonClass =
  "inline-flex items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-11";
export const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-base md:text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-50";
/** Grows a small icon button's tap area to about 40px on a touch screen
 * without making its table row any taller. */
export const touchHitAreaClass = "[@media(pointer:coarse)]:-m-2.5 [@media(pointer:coarse)]:p-3";

export function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{title}</h3>
      {children}
    </section>
  );
}

/** One "label … value" line of a card. */
export function CardRow({
  label,
  children,
  className,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx("flex items-center justify-between gap-3 py-0.5 text-sm", className)}>
      <span className="text-gray-500">{label}</span>
      <span className="min-w-0 text-right tabular-nums text-gray-900">{children}</span>
    </div>
  );
}

export function ToggleSwitch({
  label,
  help,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  help?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Field disabled={disabled} className="py-1">
      <div className="flex items-center justify-between gap-3">
        <Label className="text-sm text-gray-800">{label}</Label>
        <Switch
          checked={checked}
          onChange={onChange}
          className={clsx(
            checked ? "bg-indigo-600" : "bg-gray-300",
            "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
          )}
        >
          <span
            className={clsx(
              checked ? "translate-x-[18px]" : "translate-x-0.5",
              "inline-block h-4 w-4 rounded-full bg-white shadow transition-transform"
            )}
          />
        </Switch>
      </div>
      {help && <Description className="mt-0.5 text-xs text-gray-500">{help}</Description>}
    </Field>
  );
}

const BANNER_TONE = {
  critical: { box: "border-red-200 bg-red-50 text-red-900", icon: "text-red-600" },
  warning: { box: "border-amber-200 bg-amber-50 text-amber-900", icon: "text-amber-600" },
  info: { box: "border-sky-200 bg-sky-50 text-sky-900", icon: "text-sky-600" },
};

/** A one-line notice with its action at the end; `children` below it is for
 * the rare banner that needs a form (confirming a bank account). */
export function CompactBanner({
  tone,
  message,
  action,
  onDismiss,
  children,
}: {
  tone: keyof typeof BANNER_TONE;
  message: React.ReactNode;
  action?: React.ReactNode;
  onDismiss?: () => void;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const Icon = tone === "info" ? Info : AlertTriangle;
  return (
    <div role={tone === "info" ? "status" : "alert"} className={clsx("rounded-lg border px-3 py-2 text-sm", BANNER_TONE[tone].box)}>
      <div className="flex items-start gap-2.5">
        <Icon className={clsx("mt-0.5 h-4 w-4 shrink-0", BANNER_TONE[tone].icon)} aria-hidden />
        <div className="min-w-0 flex-1">{message}</div>
        {action && <div className="shrink-0">{action}</div>}
        {onDismiss && (
          <button type="button" onClick={onDismiss} className="shrink-0 opacity-60 hover:opacity-100" aria-label={t("close", { defaultValue: "Close" })}>
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
      {children && <div className="mt-2 pl-6">{children}</div>}
    </div>
  );
}

/** A collapsed-by-default section of the right column, unless it holds
 * something the reader has to act on. */
export function Accordion({
  title,
  aside,
  defaultOpen,
  children,
}: {
  title: string;
  aside?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Disclosure as="div" defaultOpen={defaultOpen}>
      <DisclosureButton className="group flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-gray-700 hover:bg-gray-50">
        <ChevronRight className="h-4 w-4 text-gray-400 transition-transform group-data-[open]:rotate-90" aria-hidden />
        {title}
        {aside !== undefined && <span className="ml-auto text-xs font-normal text-gray-400">{aside}</span>}
      </DisclosureButton>
      <DisclosurePanel className="px-4 pb-3 pl-10">{children}</DisclosurePanel>
    </Disclosure>
  );
}

/** The reason a rejection or a removal must carry, typed in before it is confirmed. */
export function ReasonBox({
  label,
  help,
  value,
  onChange,
  confirmLabel,
  cancelLabel,
  disabled,
  onConfirm,
  onCancel,
}: {
  label: string;
  help: string;
  value: string;
  onChange: (value: string) => void;
  confirmLabel: string;
  cancelLabel: string;
  disabled: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const id = React.useId();
  return (
    <div className="space-y-2 rounded-lg border border-red-200 bg-red-50/40 p-3">
      <label htmlFor={id} className="block text-sm font-medium text-gray-800">
        {label}
      </label>
      <textarea id={id} rows={2} className={inputClass} value={value} onChange={(e) => onChange(e.target.value)} />
      <p className="text-xs text-gray-500">{help}</p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="inline-flex items-center rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-11"
          disabled={disabled || !value.trim()}
          onClick={onConfirm}
        >
          {confirmLabel}
        </button>
        <button type="button" className={buttonClass} onClick={onCancel}>
          {cancelLabel}
        </button>
      </div>
    </div>
  );
}
